import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '@/features/auth';
import { isNative } from '@/lib/capacitor';
import { clearLimitsCache } from '@/hooks/useLimits';
import type { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import {
  initRevenueCat,
  loginRevenueCat,
  logoutRevenueCat,
  getOfferings,
  purchasePackage,
  restorePurchases,
  checkPremiumEntitlement,
  getCustomerInfo,
  isRevenueCatAvailable,
  REVENUECAT_PRODUCTS,
} from '@/lib/revenuecat';

export interface IAPPrice {
  identifier: string;
  priceString: string;
  price: number;
  currencyCode: string;
  offeringIdentifier: string;
}

export interface IAPPricesData {
  monthly: IAPPrice | null;
  sixMonth: IAPPrice | null;
  yearly: IAPPrice | null;
  // Formatted prices
  monthlyFormatted: string;
  sixMonthFormatted: string;
  sixMonthPerMonthFormatted: string;
  yearlyFormatted: string;
  yearlyPerMonthFormatted: string;
  // Trial info
  sixMonthTrialDays: number;
  yearlyTrialDays: number;
  // Savings calculations
  sixMonthSavingsPercent: number;
  yearlySavingsPercent: number;
}

// Initialize RevenueCat and sync with auth state
export function useRevenueCatInit() {
  const { user } = useAuth();

  useEffect(() => {
    if (!isNative) return;

    const init = async () => {
      try {
        // Initialize with user ID if logged in
        await initRevenueCat(user?.id);

        // If user is logged in, sync with RevenueCat
        if (user?.id) {
          await loginRevenueCat(user.id);
        }
      } catch (error) {
        console.error('Failed to initialize RevenueCat:', error);
      }
    };

    init();
  }, [user?.id]);

  // Handle logout
  useEffect(() => {
    if (!isNative) return;

    // When user becomes null (logged out), log out of RevenueCat
    if (!user?.id && isRevenueCatAvailable()) {
      logoutRevenueCat().catch(console.error);
    }
  }, [user?.id]);
}

// Get IAP prices from RevenueCat offerings
export function useIAPPrices() {
  return useQuery({
    queryKey: ['iap-prices'],
    queryFn: async (): Promise<IAPPricesData> => {
      if (!isNative) {
        throw new Error('IAP only available on native platforms');
      }

      const offerings = await getOfferings();

      if (!offerings?.all || Object.keys(offerings.all).length === 0) {
        throw new Error('No offerings available');
      }

      // Look packages up via the offering identifier — on Android product
      // identifiers are `<offeringId>:<basePlanId>`, so matching against the
      // product identifier directly only works on iOS.
      const findBasePackage = (offeringId: string): PurchasesPackage | undefined =>
        offerings.all?.[offeringId]?.availablePackages?.[0];

      const monthlyPkg = findBasePackage(REVENUECAT_PRODUCTS.MONTHLY);
      const sixMonthPkg = findBasePackage(REVENUECAT_PRODUCTS.SIX_MONTH);
      const yearlyPkg = findBasePackage(REVENUECAT_PRODUCTS.YEARLY);

      const mapPackageToPrice = (pkg: PurchasesPackage | undefined): IAPPrice | null => {
        if (!pkg) return null;
        return {
          identifier: pkg.product.identifier,
          priceString: pkg.product.priceString,
          price: pkg.product.price,
          currencyCode: pkg.product.currencyCode,
          offeringIdentifier: pkg.offeringIdentifier || offerings.current?.identifier || 'default',
        };
      };

      const monthly = mapPackageToPrice(monthlyPkg);
      const sixMonth = mapPackageToPrice(sixMonthPkg);
      const yearly = mapPackageToPrice(yearlyPkg);

      // Calculate per-month prices
      const sixMonthPerMonth = sixMonth ? sixMonth.price / 6 : 0;
      const yearlyPerMonth = yearly ? yearly.price / 12 : 0;

      // Calculate savings
      const calculateSavings = (monthlyPrice: number, totalPrice: number, months: number): number => {
        const totalIfMonthly = monthlyPrice * months;
        if (totalIfMonthly <= 0) return 0;
        return Math.round(((totalIfMonthly - totalPrice) / totalIfMonthly) * 100);
      };

      const sixMonthSavings = monthly && sixMonth
        ? calculateSavings(monthly.price, sixMonth.price, 6)
        : 37; // Fallback

      const yearlySavings = monthly && yearly
        ? calculateSavings(monthly.price, yearly.price, 12)
        : 50; // Fallback

      // Format price helper
      const formatPrice = (price: number, currency: string): string => {
        return new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: currency || 'EUR',
        }).format(price);
      };

      return {
        monthly,
        sixMonth,
        yearly,
        monthlyFormatted: monthly?.priceString || '3,99 €',
        sixMonthFormatted: sixMonth?.priceString || '14,99 €',
        sixMonthPerMonthFormatted: sixMonth
          ? formatPrice(sixMonthPerMonth, sixMonth.currencyCode)
          : '2,50 €',
        yearlyFormatted: yearly?.priceString || '23,99 €',
        yearlyPerMonthFormatted: yearly
          ? formatPrice(yearlyPerMonth, yearly.currencyCode)
          : '2,00 €',
        sixMonthTrialDays: 7, // Configured in App Store Connect / Google Play
        yearlyTrialDays: 7, // Configured in App Store Connect / Google Play
        sixMonthSavingsPercent: sixMonthSavings,
        yearlySavingsPercent: yearlySavings,
      };
    },
    enabled: isNative && isRevenueCatAvailable(),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000,
    retry: 2,
  });
}

// Purchase an IAP product
export function usePurchaseIAP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      if (!isNative || !isRevenueCatAvailable()) {
        throw new Error('IAP not available');
      }

      // `productId` is the offering identifier (e.g. `pp_6month`).
      // On Android the underlying product identifier has the form
      // `<offeringId>:<basePlanId>` (e.g. `pp_6month:pp-6month-base`),
      // so we look the package up via the offering rather than by product id.
      const offerings = await getOfferings();
      const offering = offerings?.all?.[productId] ?? (offerings?.current?.identifier === productId ? offerings.current : undefined);
      const pkg: PurchasesPackage | undefined = offering?.availablePackages?.[0];

      if (!pkg) {
        console.error('RevenueCat: Offering not found or has no packages:', productId);
        console.error('RevenueCat: Available offerings:', Object.keys(offerings?.all || {}));
        throw new Error(`Product not found: ${productId}`);
      }

      // Pass the full package object to purchasePackage
      const result = await purchasePackage(pkg);

      if (result.cancelled) {
        throw new Error('PURCHASE_CANCELLED');
      }

      if (!result.success) {
        throw result.error || new Error('Purchase failed');
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate subscription queries to refresh status
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['iap-entitlement'] });
      // Der Limit-Cache liegt nicht in react-query und wuerde sonst bis zu
      // 5 Minuten das alte Free-Limit weiterreichen.
      clearLimitsCache();
    },
  });
}

// Restore purchases
export function useRestorePurchases() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!isNative || !isRevenueCatAvailable()) {
        throw new Error('IAP not available');
      }

      return restorePurchases();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['iap-entitlement'] });
      clearLimitsCache();
    },
  });
}

// Check premium entitlement status
export function useIAPEntitlement() {
  return useQuery({
    queryKey: ['iap-entitlement'],
    queryFn: async () => {
      if (!isNative || !isRevenueCatAvailable()) {
        return { hasPremium: false, customerInfo: null };
      }

      const hasPremium = await checkPremiumEntitlement();
      const customerInfo = await getCustomerInfo();

      return { hasPremium, customerInfo };
    },
    enabled: isNative && isRevenueCatAvailable(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Export product IDs for use in components
export { REVENUECAT_PRODUCTS };
